import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Backdrop,
  CircularProgress
} from '@mui/material';
import MainCard from '../../components/MainCard';
import Sidebar from "../../menu-items/Sidebar";
import axios from 'axios';
import Cookies from "js-cookie";

const CompletedConsentBundles = () => {
  const [data, setData] = useState([]); 
  const [page, setPage] = useState(0);                  // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);    // loader for export

  // ---------- Core page fetch (server-side pagination) ----------
  const fetchData = async (currentPage = 1, limit = 100) => {
    const token = axios.defaults.headers.common.Authorization;
    const url = `${process.env.REACT_APP_BASE_URL}admin/reports/completed_consent_bundles`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'application/json',
        },
        params: {
          offset: currentPage, // API expects 1-based page number
          limit: limit
        }
      });

      setData(response.data?.results ?? []);
      setTotalCount(response.data?.total ?? 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    fetchData(page + 1, rowsPerPage); // MUI uses 0-based, API uses 1-based
  }, [page, rowsPerPage]);

  // ---------- Pagination handlers ----------
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0); // reset to first page
  };

  // ---------- CSV: download current page only ----------
  const downloadPageAsCSV = () => {
    const headers = ['#', 'Consent bundle code', 'Practice', 'Created At', 'Status'];
    const csvRows: string[] = [headers.join(',')];

    data.forEach((row, index) => {
      const cells = [
        (index + 1 + page * rowsPerPage).toString(),
        row?.consent_bundle_code ?? '',
        row?.practice ?? '',
        row?.created_at ?? '',
        row?.status ?? ''
      ].map((v) =>
        typeof v === 'string' && (v.includes(',') || v.includes('"'))
          ? `"${v.replace(/"/g, '""')}"`
          : v
      );
      csvRows.push(cells.join(','));
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Completed_Consent_Bundles_Page.csv';
    link.click();
  };

  // ---------- Helpers for ALL export ----------
  const normalizePayload = (payload: any) => {
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : Object.values(payload ?? {});
    const total =
      typeof payload?.total === 'number'
        ? payload.total
        : Array.isArray(payload?.results)
        ? payload.results.length
        : Array.isArray(payload?.data)
        ? payload.data.length
        : Array.isArray(payload)
        ? payload.length
        : results.length;
    return { results, total };
  };

  const fetchPageAll = async (pageIndex1Based: number, limit: number) => {
    const token = axios.defaults.headers.common.Authorization;
    const url = `${process.env.REACT_APP_BASE_URL}admin/reports/completed_consent_bundles`;
    const res = await axios.get(url, {
      headers: {
        Authorization: `${token}`,
        'Content-Type': 'application/json',
      },
      params: {
        offset: pageIndex1Based,
        limit,
      },
    });
    return normalizePayload(res.data);
  };

  // ---------- CSV: download ALL pages ----------
  const downloadAllAsCSV = async () => {
    setExporting(true);
    try {
      const pageSize = 500; // tune to your backend cap
      const first = await fetchPageAll(1, pageSize);

      const allRows: any[] = [...first.results];
      const total = first.total;
      const totalPages = Math.ceil(total / pageSize);

      for (let p = 2; p <= totalPages; p++) {
        const { results } = await fetchPageAll(p, pageSize);
        allRows.push(...results);
      }

      const headers = ['#', 'Consent bundle code', 'Practice', 'Created At', 'Status'];
      const csvRows: string[] = [headers.join(',')];

      allRows.forEach((row, idx) => {
        const cells = [
          (idx + 1).toString(),
          row?.consent_bundle_code ?? '',
          row?.practice ?? '',
          row?.created_at ?? '',
          row?.status ?? ''
        ].map((v) =>
          typeof v === 'string' && (v.includes(',') || v.includes('"'))
            ? `"${v.replace(/"/g, '""')}"`
            : v
        );
        csvRows.push(cells.join(','));
      });

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'Completed_Consent_Bundles_ALL.csv';
      link.click();
    } catch (err) {
      console.error('Download all CSV failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ border: '1px solid #ccc', padding: 5, height: '100vh' }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
              Completed Consent Bundles
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={downloadPageAsCSV}
                sx={{ textTransform: 'none' }}
                disabled={exporting}
              >
                Download Page CSV
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={downloadAllAsCSV}
                sx={{ textTransform: 'none' }}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={18} /> : null}
              >
                {exporting ? 'Preparing CSV…' : 'Download ALL CSV'}
              </Button>
            </Box>
          </Box>

          <MainCard title="">
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '15px',
                boxShadow: 3,
                overflow: 'hidden',
                marginTop: 2,
              }}
            >
              <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2', color: '#fff' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Consent bundle code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Practice</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created at</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, index) => (
                      <TableRow
                        key={`${row?.consent_bundle_code ?? 'row'}-${index}`}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            cursor: 'pointer',
                          },
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{row?.consent_bundle_code}</TableCell>
                        <TableCell>{row?.practice}</TableCell>
                        <TableCell>{row?.created_at}</TableCell>
                        <TableCell>{row?.status}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>

      {/* Full-screen loader during ALL export */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={exporting}
      >
        <CircularProgress />
      </Backdrop>
    </Box>
  );
};

export default CompletedConsentBundles;
